import { apiClient } from './apiClient';
import type { User } from '@/types';

export interface UserSession {
  id: string;
  ip?: string | null;
  userAgent?: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
  isActive: boolean;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// Strip empty strings before sending
const clean = (obj: any) => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj ?? {})) {
    if (v === '' || v === undefined) continue;
    out[k] = v;
  }
  return out;
};

export const userService = {
  async list(params: { page?: number; pageSize?: number; search?: string } = {}): Promise<Paginated<User>> {
    const { data } = await apiClient.get<Paginated<User>>('/users', { params });
    return data;
  },

  async getById(id: string): Promise<User> {
    const { data } = await apiClient.get<User>(`/users/${id}`);
    return data;
  },

  async create(payload: {
    username: string;
    fullName: string;
    email: string;
    departmentId: string;
    phone?: string;
    position?: string;
    password?: string;
    roles?: string[];
    gender?: 'UNKNOWN' | 'MALE' | 'FEMALE';  // V1.0.48
  }): Promise<User> {
    const { data } = await apiClient.post<User>('/users', clean(payload));
    return data;
  },

  async update(id: string, payload: {
    fullName?: string;
    email?: string;
    phone?: string;
    departmentId?: string;
    position?: string;
    avatarUrl?: string;
    roles?: string[];
    otpRequired?: boolean;
    gender?: 'UNKNOWN' | 'MALE' | 'FEMALE';  // V1.0.48
  }): Promise<User> {
    const { data } = await apiClient.patch<User>(`/users/${id}`, clean(payload));
    return data;
  },

  async setStatus(id: string, status: 'ACTIVE' | 'LOCKED' | 'INACTIVE'): Promise<User> {
    const { data } = await apiClient.post<User>(`/users/${id}/status`, { status });
    return data;
  },

  async resetPassword(id: string, newPassword?: string): Promise<{ password: string; message: string }> {
    const { data } = await apiClient.post<{ password: string; message: string }>(
      `/users/${id}/reset-password`,
      { newPassword: newPassword || null }
    );
    return data;
  },

  async assignRoles(id: string, roles: string[]): Promise<void> {
    await apiClient.post(`/users/${id}/roles`, { roles });
  },

  async getSessions(id: string): Promise<UserSession[]> {
    const { data } = await apiClient.get<UserSession[]>(`/users/${id}/sessions`);
    return data;
  },

  async revokeSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/users/sessions/${sessionId}`);
  },

  // ============================================================
  // V1.0.33: Import nhân viên từ Excel
  // ============================================================

  async importUsers(file: File): Promise<ImportUsersResult> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<ImportUsersResult>(
      '/users/import',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  /** Tải file Excel mẫu để import */
  downloadImportTemplate(): void {
    // Mở tab mới với endpoint download (browser tự handle Content-Disposition)
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL ?? '/api';
    const url = `${baseUrl}/users/import-template`;
    // Dùng fetch + blob để giữ Authorization header
    apiClient.get('/users/import-template', { responseType: 'blob' }).then(res => {
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'mau-import-nhan-vien.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    });
  },

  // V1.0.63: 1 user quản lý nhiều khoa
  async getManagedDepartments(userId: string): Promise<Array<{ id: string; code: string; name: string }>> {
    const { data } = await apiClient.get(`/users/${userId}/managed-departments`);
    return data;
  },

  async updateManagedDepartments(userId: string, departmentIds: string[]): Promise<void> {
    await apiClient.put(`/users/${userId}/managed-departments`, { departmentIds });
  },
};

// V1.0.33: Types cho kết quả import
export interface ImportUserError {
  rowNumber: number;
  username?: string | null;
  field: string;
  message: string;
}

export interface ImportUsersResult {
  totalRows: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  errors: ImportUserError[];
  createdUsernames: string[];
}
